import React, { useState, useEffect } from 'react';
import { 
  PackagePlus, 
  Users, 
  UserCheck,
  Store, 
  MessageSquare, 
  Megaphone, 
  CreditCard, 
  Upload, 
  HardDrive, 
  ChevronUp, 
  ChevronDown, 
  Shield, 
  Box, 
  Truck, 
  X,
  Sparkles,
  Sliders,
  ChevronRight,
  DollarSign,
  BarChart3,
  Mail,
  Server,
  Inbox,
  Search,
  Ticket,
  FileSpreadsheet,
  Database,
  TrendingUp,
  Printer,
  ShoppingBag,
  BookOpen,
  Headphones,
  Film,
  Gift,
  Award
} from 'lucide-react';

interface AdminSidebarProps {
  isAdmin: boolean;
  isLoggedIn: boolean;
  onOpenAdminEntry?: () => void;
  onOpenPackageExcelImport?: () => void;
  onOpenClientImportModal?: () => void;
  onOpenAddStoreProductModal?: () => void;
  onOpenCouponsModal?: () => void;
  onOpenExcelProductImportModal?: () => void;
  onOpenWhatsAppModal?: () => void;
  onOpenGoogleDriveModal?: () => void;
  onOpenLogoModal?: () => void;
  onOpenPaymentMethodsModal?: () => void;
  onOpenExchangeRateModal?: () => void;
  onOpenHomePopupModal?: () => void;
  onOpenEmailMarketingModal?: () => void;
  onOpenSmtpConfigModal?: () => void;
  onOpenSearchConsoleModal?: () => void;
  onOpenOnamaeDbModal?: () => void;
  activeTab?: string;
  setActiveTab?: (tab: string) => void;
  pendingShipmentsCount?: number;
  pendingPsOrdersCount?: number;
}

export const AdminSidebar: React.FC<AdminSidebarProps> = ({
  isAdmin,
  isLoggedIn,
  onOpenAdminEntry,
  onOpenPackageExcelImport,
  onOpenClientImportModal,
  onOpenAddStoreProductModal,
  onOpenCouponsModal,
  onOpenExcelProductImportModal,
  onOpenWhatsAppModal,
  onOpenGoogleDriveModal,
  onOpenLogoModal,
  onOpenPaymentMethodsModal,
  onOpenExchangeRateModal,
  onOpenHomePopupModal,
  onOpenEmailMarketingModal,
  onOpenSmtpConfigModal,
  onOpenSearchConsoleModal,
  onOpenOnamaeDbModal,
  activeTab,
  setActiveTab,
  pendingShipmentsCount = 0,
  pendingPsOrdersCount = 0
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  useEffect(() => {
    const handleOpen = () => setIsExpanded(true);
    const handleToggle = () => setIsExpanded(prev => !prev);
    window.addEventListener('openAdminSidebar', handleOpen);
    window.addEventListener('toggleAdminSidebar', handleToggle);
    return () => {
      window.removeEventListener('openAdminSidebar', handleOpen);
      window.removeEventListener('toggleAdminSidebar', handleToggle);
    };
  }, []);

  if (!isAdmin || !isLoggedIn) return null;

  return (
    <div className="fixed bottom-3 sm:bottom-4 left-1/2 -translate-x-1/2 z-50 max-w-[95vw] transition-all duration-300">
      
      {/* ----------------- EXPANDED SUSPENDED PANEL STATE (PAINEL DE GESTÃO COMPLETO) ----------------- */}
      {isExpanded ? (
        <div className="bg-slate-900/95 backdrop-blur-xl border border-slate-700/80 shadow-2xl rounded-3xl p-4 sm:p-5 text-white w-[92vw] max-w-5xl max-h-[84vh] overflow-y-auto space-y-4 animate-scale-up border-t-2 border-t-amber-500/80">
          
          {/* Header Bar */}
          <div className="flex items-center justify-between pb-3 border-b border-slate-800">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-2xl bg-amber-500/20 border border-amber-500/40 text-amber-400 flex items-center justify-center shrink-0 font-bold shadow-xs">
                <Shield className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-black text-white">Painel de Gestão Administrador</h3>
                  <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-400/30 text-[9px] font-extrabold uppercase font-mono tracking-wider">
                    Modo Suspenso
                  </span>
                </div>
                <p className="text-[11px] text-slate-400">
                  Todas as ferramentas de gestão, catálogo, marketing, banco de dados e automações
                </p>
              </div>
            </div>

            <button
              onClick={() => setIsExpanded(false)}
              className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors cursor-pointer flex items-center gap-1.5 text-xs font-bold border border-slate-700"
              title="Recolher Painel Admin"
              id="admin-panel-close-btn"
            >
              <span>Minimizar</span>
              <ChevronDown className="w-4 h-4" />
            </button>
          </div>

          {/* Quick Core Shortcuts Ribbon */}
          {setActiveTab && (
            <div className="p-2.5 bg-slate-950/60 rounded-2xl border border-slate-800/80 flex flex-wrap items-center justify-between gap-2">
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider flex items-center gap-1">
                <Sliders className="w-3.5 h-3.5 text-amber-400" />
                <span>Acesso Rápido Direto:</span>
              </span>

              <div className="flex items-center gap-1.5 overflow-x-auto py-0.5">
                <button
                  onClick={() => {
                    setActiveTab('admin-wordpress');
                    setIsExpanded(false);
                  }}
                  className={`hidden lg:flex px-3 py-1.5 rounded-xl text-xs font-black items-center gap-1.5 transition-all cursor-pointer shadow-md ${
                    activeTab === 'admin-wordpress' ? 'bg-amber-400 text-slate-950 font-black ring-2 ring-amber-300' : 'bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 hover:brightness-110'
                  }`}
                  title="Abrir o Painel do Administrador Completo estilo WordPress (Exclusivo Computador)"
                >
                  <span className="w-4 h-4 rounded-full bg-slate-950 text-white flex items-center justify-center font-serif text-[10px] font-black">W</span>
                  <span>Painel WordPress (wp-admin)</span>
                </button>

                <button
                  onClick={() => {
                    setActiveTab('admin-shipments');
                    setIsExpanded(false);
                  }}
                  className={`px-2.5 py-1 rounded-lg text-xs font-bold flex items-center gap-1 transition-all cursor-pointer ${
                    activeTab === 'admin-shipments' ? 'bg-sky-500 text-slate-950 font-black' : 'bg-slate-800 text-sky-300 hover:bg-slate-700'
                  }`}
                >
                  <Truck className="w-3.5 h-3.5" />
                  <span>Pedidos de Envios</span>
                  {pendingShipmentsCount > 0 && (
                    <span className="px-1.5 py-0.2 text-[9px] font-black bg-rose-500 text-white rounded-full animate-pulse">
                      {pendingShipmentsCount}
                    </span>
                  )}
                </button>

                <button
                  onClick={() => {
                    setActiveTab('sato-labels');
                    setIsExpanded(false);
                  }}
                  className={`px-2.5 py-1 rounded-lg text-xs font-bold flex items-center gap-1 transition-all cursor-pointer ${
                    activeTab === 'sato-labels' ? 'bg-amber-400 text-slate-950 font-black' : 'bg-slate-800 text-amber-300 hover:bg-slate-700'
                  }`}
                  title="Editor Visual de Etiquetas para Impressora Térmica SATO PW208 (203 DPI / SBPL)"
                >
                  <Printer className="w-3.5 h-3.5 text-slate-950" />
                  <span>Etiqueta SATO</span>
                </button>

                <button
                  onClick={() => {
                    setActiveTab('admin-clients');
                    setIsExpanded(false);
                  }}
                  className={`px-2.5 py-1 rounded-lg text-xs font-bold flex items-center gap-1 transition-all cursor-pointer ${
                    activeTab === 'admin-clients' ? 'bg-amber-500 text-slate-950 font-black' : 'bg-slate-800 text-amber-300 hover:bg-slate-700'
                  }`}
                >
                  <Users className="w-3.5 h-3.5" />
                  <span>Lista de Clientes</span>
                </button>

                <button
                  onClick={() => {
                    setActiveTab('personal-shopper');
                    setIsExpanded(false);
                  }}
                  className={`px-2.5 py-1 rounded-lg text-xs font-bold flex items-center gap-1 transition-all cursor-pointer relative ${
                    activeTab === 'personal-shopper'
                      ? 'bg-indigo-600 text-white font-black'
                      : pendingPsOrdersCount > 0
                        ? 'bg-amber-500/20 text-amber-300 border border-amber-400 animate-pulse'
                        : 'bg-slate-800 text-indigo-300 hover:bg-slate-700'
                  }`}
                >
                  <ShoppingBag className="w-3.5 h-3.5" />
                  <span>Personal Shopper</span>
                  {pendingPsOrdersCount > 0 && (
                    <span className="px-1.5 py-0.2 text-[9px] font-black bg-rose-600 text-white rounded-full">
                      {pendingPsOrdersCount}
                    </span>
                  )}
                </button>

                <button
                  onClick={() => {
                    setActiveTab('admin-packages');
                    setIsExpanded(false);
                  }}
                  className={`px-2.5 py-1 rounded-lg text-xs font-bold flex items-center gap-1 transition-all cursor-pointer ${
                    activeTab === 'admin-packages' ? 'bg-emerald-500 text-slate-950 font-black' : 'bg-slate-800 text-emerald-300 hover:bg-slate-700'
                  }`}
                >
                  <Box className="w-3.5 h-3.5" />
                  <span>Estoque Pacotes</span>
                </button>

                <button
                  onClick={() => {
                    setActiveTab('admin-finances');
                    setIsExpanded(false);
                  }}
                  className={`px-2.5 py-1 rounded-lg text-xs font-bold flex items-center gap-1 transition-all cursor-pointer ${
                    activeTab === 'admin-finances' ? 'bg-emerald-500 text-slate-950 font-black' : 'bg-slate-800 text-emerald-300 hover:bg-slate-700'
                  }`}
                >
                  <DollarSign className="w-3.5 h-3.5" />
                  <span>Financeiro & Balanço</span>
                </button>

                {onOpenAdminEntry && (
                  <button
                    onClick={() => {
                      onOpenAdminEntry();
                      setIsExpanded(false);
                    }}
                    className="px-2.5 py-1 bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 font-black text-xs rounded-lg flex items-center gap-1 transition-all hover:scale-105 cursor-pointer shadow-sm"
                  >
                    <PackagePlus className="w-3.5 h-3.5" />
                    <span>Dar Entrada</span>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Categorized Admin Management Tools Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            
            {/* 1. 🏪 NOSSA LOJA, PRODUTOS & CUPONS */}
            <div className="p-3 bg-slate-800/60 border border-slate-700/70 rounded-2xl space-y-2">
              <div className="flex items-center gap-2 pb-1.5 border-b border-slate-700/60">
                <Store className="w-4 h-4 text-red-400" />
                <span className="text-xs font-black text-red-300 uppercase tracking-wider">Loja & Produtos</span>
              </div>

              <div className="space-y-1.5">
                {setActiveTab && (
                  <button
                    onClick={() => {
                      setActiveTab('nossa-loja');
                      setIsExpanded(false);
                    }}
                    className="w-full p-2 bg-slate-900/80 hover:bg-slate-900 border border-red-500/30 hover:border-red-400 rounded-xl text-left transition-all cursor-pointer flex items-center gap-2.5"
                    id="panel-btn-go-to-store"
                  >
                    <div className="w-7 h-7 rounded-lg bg-red-500 text-white flex items-center justify-center shrink-0">
                      <Store className="w-4 h-4" />
                    </div>
                    <div className="overflow-hidden">
                      <span className="text-xs font-bold text-red-200 block truncate">Atalho pra Nossa Loja 🛍️</span>
                      <span className="text-[10px] text-slate-400 block truncate">Vitrine de produtos japoneses</span>
                    </div>
                  </button>
                )}

                {onOpenAddStoreProductModal && (
                  <button
                    onClick={() => {
                      onOpenAddStoreProductModal();
                      setIsExpanded(false);
                    }}
                    className="w-full p-2 bg-slate-900/80 hover:bg-slate-900 border border-slate-700 hover:border-amber-400 rounded-xl text-left transition-all cursor-pointer flex items-center gap-2.5"
                    id="panel-btn-add-store-product"
                  >
                    <div className="w-7 h-7 rounded-lg bg-red-500/20 text-red-400 flex items-center justify-center shrink-0">
                      <Store className="w-4 h-4" />
                    </div>
                    <div className="overflow-hidden">
                      <span className="text-xs font-bold text-slate-200 block truncate">Cadastrar Produto na Loja</span>
                      <span className="text-[10px] text-slate-400 block truncate">Adicionar novo item no catálogo</span>
                    </div>
                  </button>
                )}

                {onOpenExcelProductImportModal && (
                  <button
                    onClick={() => {
                      if (setActiveTab) setActiveTab('nossa-loja');
                      onOpenExcelProductImportModal();
                      setIsExpanded(false);
                    }}
                    className="w-full p-2 bg-slate-900/80 hover:bg-slate-900 border border-emerald-500/40 hover:border-emerald-400 rounded-xl text-left transition-all cursor-pointer flex items-center gap-2.5"
                    id="panel-btn-excel-product-import"
                  >
                    <div className="w-7 h-7 rounded-lg bg-emerald-500/20 text-emerald-300 flex items-center justify-center shrink-0">
                      <FileSpreadsheet className="w-4 h-4" />
                    </div>
                    <div className="overflow-hidden">
                      <span className="text-xs font-bold text-emerald-300 block truncate">Importar/Exportar Excel 📊</span>
                      <span className="text-[10px] text-slate-400 block truncate">Catálogo de produtos em lote</span>
                    </div>
                  </button>
                )}

                {onOpenCouponsModal && (
                  <button
                    onClick={() => {
                      onOpenCouponsModal();
                      setIsExpanded(false);
                    }}
                    className="w-full p-2 bg-slate-900/80 hover:bg-slate-900 border border-amber-500/40 hover:border-amber-400 rounded-xl text-left transition-all cursor-pointer flex items-center gap-2.5"
                    id="panel-btn-coupons-management"
                  >
                    <div className="w-7 h-7 rounded-lg bg-amber-500/20 text-amber-300 flex items-center justify-center shrink-0">
                      <Ticket className="w-4 h-4" />
                    </div>
                    <div className="overflow-hidden">
                      <span className="text-xs font-bold text-amber-300 block truncate">Cupons de Desconto 🎟️</span>
                      <span className="text-[10px] text-slate-400 block truncate">Criar promoções & códigos</span>
                    </div>
                  </button>
                )}
              </div>
            </div>

            {/* 2. 💱 CÂMBIO, TAXAS & FORMAS DE PAGAMENTO */}
            <div className="p-3 bg-slate-800/60 border border-slate-700/70 rounded-2xl space-y-2">
              <div className="flex items-center gap-2 pb-1.5 border-b border-slate-700/60">
                <TrendingUp className="w-4 h-4 text-amber-400" />
                <span className="text-xs font-black text-amber-300 uppercase tracking-wider">Câmbio & Pagamentos</span>
              </div>

              <div className="space-y-1.5">
                {onOpenExchangeRateModal && (
                  <button
                    onClick={() => {
                      onOpenExchangeRateModal();
                      setIsExpanded(false);
                    }}
                    className="w-full p-2 bg-slate-900/80 hover:bg-slate-900 border border-amber-500/40 hover:border-amber-400 rounded-xl text-left transition-all cursor-pointer flex items-center gap-2.5"
                    id="panel-btn-exchange-rate"
                  >
                    <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 text-slate-950 font-black flex items-center justify-center shrink-0">
                      <TrendingUp className="w-4 h-4 text-slate-950" />
                    </div>
                    <div className="overflow-hidden">
                      <span className="text-xs font-bold text-amber-300 block truncate">Cotação de Câmbio 💱</span>
                      <span className="text-[10px] text-slate-400 block truncate">Taxa JPY/BRL com margem spread %</span>
                    </div>
                  </button>
                )}

                {onOpenPaymentMethodsModal && (
                  <button
                    onClick={() => {
                      onOpenPaymentMethodsModal();
                      setIsExpanded(false);
                    }}
                    className="w-full p-2 bg-slate-900/80 hover:bg-slate-900 border border-slate-700 hover:border-amber-400 rounded-xl text-left transition-all cursor-pointer flex items-center gap-2.5"
                    id="panel-btn-payment-methods"
                  >
                    <div className="w-7 h-7 rounded-lg bg-amber-500/20 text-amber-300 flex items-center justify-center shrink-0">
                      <CreditCard className="w-4 h-4" />
                    </div>
                    <div className="overflow-hidden">
                      <span className="text-xs font-bold text-slate-200 block truncate">Formas de Pagamento 💳</span>
                      <span className="text-[10px] text-slate-400 block truncate">PIX, Cartão, Carteiras Digitais</span>
                    </div>
                  </button>
                )}

                {setActiveTab && (
                  <button
                    onClick={() => {
                      setActiveTab('admin-finances');
                      setIsExpanded(false);
                    }}
                    className="w-full p-2 bg-slate-900/80 hover:bg-slate-900 border border-emerald-500/30 hover:border-emerald-400 rounded-xl text-left transition-all cursor-pointer flex items-center gap-2.5"
                    id="panel-btn-admin-finances-cat"
                  >
                    <div className="w-7 h-7 rounded-lg bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0">
                      <DollarSign className="w-4 h-4" />
                    </div>
                    <div className="overflow-hidden">
                      <span className="text-xs font-bold text-emerald-300 block truncate">Balanço & Extratos 📊</span>
                      <span className="text-[10px] text-slate-400 block truncate">Faturamento e receitas brutas</span>
                    </div>
                  </button>
                )}
              </div>
            </div>

            {/* 3. 📢 MARKETING, NOTIFICAÇÕES & E-MAILS */}
            <div className="p-3 bg-slate-800/60 border border-slate-700/70 rounded-2xl space-y-2">
              <div className="flex items-center gap-2 pb-1.5 border-b border-slate-700/60">
                <Megaphone className="w-4 h-4 text-purple-400" />
                <span className="text-xs font-black text-purple-300 uppercase tracking-wider">Marketing & Notificações</span>
              </div>

              <div className="space-y-1.5">
                {onOpenHomePopupModal && (
                  <button
                    onClick={() => {
                      onOpenHomePopupModal();
                      setIsExpanded(false);
                    }}
                    className="w-full p-2 bg-slate-900/80 hover:bg-slate-900 border border-purple-500/30 hover:border-purple-400 rounded-xl text-left transition-all cursor-pointer flex items-center gap-2.5"
                    id="panel-btn-home-popup"
                  >
                    <div className="w-7 h-7 rounded-lg bg-purple-500/20 text-purple-400 flex items-center justify-center shrink-0">
                      <Megaphone className="w-4 h-4" />
                    </div>
                    <div className="overflow-hidden">
                      <span className="text-xs font-bold text-purple-300 block truncate">Pop-up da Tela Inicial 📢</span>
                      <span className="text-[10px] text-slate-400 block truncate">Avisos e promoções no site</span>
                    </div>
                  </button>
                )}

                {onOpenEmailMarketingModal && (
                  <button
                    onClick={() => {
                      onOpenEmailMarketingModal();
                      setIsExpanded(false);
                    }}
                    className="w-full p-2 bg-slate-900/80 hover:bg-slate-900 border border-amber-500/30 hover:border-amber-400 rounded-xl text-left transition-all cursor-pointer flex items-center gap-2.5"
                    id="panel-btn-email-marketing"
                  >
                    <div className="w-7 h-7 rounded-lg bg-amber-500/20 text-amber-300 flex items-center justify-center shrink-0">
                      <Mail className="w-4 h-4" />
                    </div>
                    <div className="overflow-hidden">
                      <span className="text-xs font-bold text-amber-200 block truncate">E-mail Marketing em Massa ✉️</span>
                      <span className="text-[10px] text-slate-400 block truncate">Envio de newsletters aos clientes</span>
                    </div>
                  </button>
                )}

                {onOpenSmtpConfigModal && (
                  <button
                    onClick={() => {
                      onOpenSmtpConfigModal();
                      setIsExpanded(false);
                    }}
                    className="w-full p-2 bg-slate-900/80 hover:bg-slate-900 border border-sky-500/30 hover:border-sky-400 rounded-xl text-left transition-all cursor-pointer flex items-center gap-2.5"
                    id="panel-btn-smtp-config"
                  >
                    <div className="w-7 h-7 rounded-lg bg-sky-500/20 text-sky-300 flex items-center justify-center shrink-0">
                      <Server className="w-4 h-4" />
                    </div>
                    <div className="overflow-hidden">
                      <span className="text-xs font-bold text-sky-200 block truncate">Configuração SMTP 📧</span>
                      <span className="text-[10px] text-slate-400 block truncate">Servidor de envio de e-mails</span>
                    </div>
                  </button>
                )}

                {setActiveTab && (
                  <button
                    onClick={() => {
                      setActiveTab('admin-email-history');
                      setIsExpanded(false);
                    }}
                    className="w-full p-2 bg-slate-900/80 hover:bg-slate-900 border border-emerald-500/30 hover:border-emerald-400 rounded-xl text-left transition-all cursor-pointer flex items-center gap-2.5"
                    id="panel-btn-email-history"
                  >
                    <div className="w-7 h-7 rounded-lg bg-emerald-500/20 text-emerald-300 flex items-center justify-center shrink-0">
                      <Inbox className="w-4 h-4" />
                    </div>
                    <div className="overflow-hidden">
                      <span className="text-xs font-bold text-emerald-200 block truncate">Histórico de E-mails 📥</span>
                      <span className="text-[10px] text-slate-400 block truncate">Status de entrega das mensagens</span>
                    </div>
                  </button>
                )}

                {onOpenWhatsAppModal && (
                  <button
                    onClick={() => {
                      onOpenWhatsAppModal();
                      setIsExpanded(false);
                    }}
                    className="w-full p-2 bg-slate-900/80 hover:bg-slate-900 border border-emerald-500/30 hover:border-emerald-400 rounded-xl text-left transition-all cursor-pointer flex items-center gap-2.5"
                    id="panel-btn-whatsapp"
                  >
                    <div className="w-7 h-7 rounded-lg bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0">
                      <MessageSquare className="w-4 h-4" />
                    </div>
                    <div className="overflow-hidden">
                      <span className="text-xs font-bold text-emerald-300 block truncate">WhatsApp API Meta 💬</span>
                      <span className="text-[10px] text-slate-400 block truncate">Notificações automáticas</span>
                    </div>
                  </button>
                )}

                {setActiveTab && (
                  <button
                    onClick={() => {
                      setActiveTab('affiliates');
                      setIsExpanded(false);
                    }}
                    className="w-full p-2 bg-slate-900/80 hover:bg-slate-900 border border-amber-500/40 hover:border-amber-400 rounded-xl text-left transition-all cursor-pointer flex items-center gap-2.5"
                    id="panel-btn-affiliates"
                  >
                    <div className="w-7 h-7 rounded-lg bg-amber-500/20 text-amber-300 flex items-center justify-center shrink-0">
                      <Award className="w-4 h-4" />
                    </div>
                    <div className="overflow-hidden">
                      <span className="text-xs font-bold text-amber-200 block truncate">Programa de Afiliados 🤝</span>
                      <span className="text-[10px] text-slate-400 block truncate">Indique e Ganhe por Suíte e PIX</span>
                    </div>
                  </button>
                )}

                {setActiveTab && (
                  <button
                    onClick={() => {
                      setActiveTab('giveaways');
                      setIsExpanded(false);
                    }}
                    className="w-full p-2 bg-slate-900/80 hover:bg-slate-900 border border-rose-500/40 hover:border-rose-400 rounded-xl text-left transition-all cursor-pointer flex items-center gap-2.5"
                    id="panel-btn-giveaways"
                  >
                    <div className="w-7 h-7 rounded-lg bg-rose-500/20 text-rose-300 flex items-center justify-center shrink-0">
                      <Gift className="w-4 h-4" />
                    </div>
                    <div className="overflow-hidden">
                      <span className="text-xs font-bold text-rose-200 block truncate">Sorteador de Brindes 🎁</span>
                      <span className="text-[10px] text-slate-400 block truncate">Sortear Suítes DJ-XXXX e Enviar Brinde</span>
                    </div>
                  </button>
                )}
              </div>
            </div>

            {/* 4. 🗄️ BANCO DE DADOS, BACKUP & INFRAESTRUTURA */}
            <div className="p-3 bg-slate-800/60 border border-slate-700/70 rounded-2xl space-y-2 md:col-span-2 lg:col-span-2">
              <div className="flex items-center gap-2 pb-1.5 border-b border-slate-700/60">
                <Database className="w-4 h-4 text-sky-400" />
                <span className="text-xs font-black text-sky-300 uppercase tracking-wider">Banco de Dados, Backup & Infraestrutura</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {onOpenOnamaeDbModal && (
                  <button
                    onClick={() => {
                      onOpenOnamaeDbModal();
                      setIsExpanded(false);
                    }}
                    className="p-2.5 bg-slate-900/80 hover:bg-slate-900 border border-red-500/50 hover:border-red-400 bg-red-950/20 rounded-xl text-left transition-all cursor-pointer flex items-start gap-2.5"
                    id="panel-btn-onamae-db"
                  >
                    <div className="w-8 h-8 rounded-lg bg-red-600 text-white font-black flex items-center justify-center shrink-0 shadow-md">
                      <Database className="w-4 h-4" />
                    </div>
                    <div className="overflow-hidden">
                      <span className="text-xs font-black text-red-300 block truncate">Central de Banco & Backup 🗄️</span>
                      <span className="text-[10px] text-slate-400 block leading-tight mt-0.5">
                        Onamae MySQL, Backup JSON e Master Reset
                      </span>
                    </div>
                  </button>
                )}

                {onOpenSearchConsoleModal && (
                  <button
                    onClick={() => {
                      onOpenSearchConsoleModal();
                      setIsExpanded(false);
                    }}
                    className="p-2.5 bg-slate-900/80 hover:bg-slate-900 border border-sky-400/40 hover:border-sky-300 rounded-xl text-left transition-all cursor-pointer flex items-start gap-2.5"
                    id="panel-btn-search-console"
                  >
                    <div className="w-8 h-8 rounded-lg bg-sky-500 text-slate-950 font-black flex items-center justify-center shrink-0 shadow-md">
                      <Search className="w-4 h-4" />
                    </div>
                    <div className="overflow-hidden">
                      <span className="text-xs font-bold text-sky-300 block truncate">Google Search Console 🔍</span>
                      <span className="text-[10px] text-slate-400 block leading-tight mt-0.5">
                        SEO, Sitemap e verificação Google
                      </span>
                    </div>
                  </button>
                )}

                {onOpenGoogleDriveModal && (
                  <button
                    onClick={() => {
                      onOpenGoogleDriveModal();
                      setIsExpanded(false);
                    }}
                    className="p-2.5 bg-slate-900/80 hover:bg-slate-900 border border-blue-500/30 hover:border-blue-400 rounded-xl text-left transition-all cursor-pointer flex items-start gap-2.5"
                    id="panel-btn-google-drive"
                  >
                    <div className="w-8 h-8 rounded-lg bg-blue-500/20 text-blue-400 flex items-center justify-center shrink-0">
                      <HardDrive className="w-4 h-4" />
                    </div>
                    <div className="overflow-hidden">
                      <span className="text-xs font-bold text-blue-300 block truncate">Google Drive Backup 📁</span>
                      <span className="text-[10px] text-slate-400 block leading-tight mt-0.5">
                        Fotos de pacotes em HD e arquivos
                      </span>
                    </div>
                  </button>
                )}

                {onOpenClientImportModal && (
                  <button
                    onClick={() => {
                      onOpenClientImportModal();
                      setIsExpanded(false);
                    }}
                    className="p-2.5 bg-slate-900/80 hover:bg-slate-900 border border-indigo-500/30 hover:border-indigo-400 rounded-xl text-left transition-all cursor-pointer flex items-start gap-2.5"
                    id="panel-btn-client-import"
                  >
                    <div className="w-8 h-8 rounded-lg bg-indigo-500/20 text-indigo-400 flex items-center justify-center shrink-0">
                      <UserCheck className="w-4 h-4" />
                    </div>
                    <div className="overflow-hidden">
                      <span className="text-xs font-bold text-indigo-300 block truncate">Importar/Exportar Clientes 👥</span>
                      <span className="text-[10px] text-slate-400 block leading-tight mt-0.5">
                        Gerenciar suítes em planilha
                      </span>
                    </div>
                  </button>
                )}

                {onOpenPackageExcelImport && (
                  <button
                    onClick={() => {
                      onOpenPackageExcelImport();
                      setIsExpanded(false);
                    }}
                    className="p-2.5 bg-slate-900/80 hover:bg-slate-900 border border-emerald-500/30 hover:border-emerald-400 rounded-xl text-left transition-all cursor-pointer flex items-start gap-2.5"
                    id="panel-btn-package-excel-import"
                  >
                    <div className="w-8 h-8 rounded-lg bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0">
                      <FileSpreadsheet className="w-4 h-4" />
                    </div>
                    <div className="overflow-hidden">
                      <span className="text-xs font-bold text-emerald-300 block truncate">Importar Pacotes em Massa 📦</span>
                      <span className="text-[10px] text-slate-400 block leading-tight mt-0.5">
                        Cadastro em lote via Excel/CSV
                      </span>
                    </div>
                  </button>
                )}

                {onOpenLogoModal && (
                  <button
                    onClick={() => {
                      onOpenLogoModal();
                      setIsExpanded(false);
                    }}
                    className="p-2.5 bg-slate-900/80 hover:bg-slate-900 border border-sky-500/30 hover:border-sky-400 rounded-xl text-left transition-all cursor-pointer flex items-start gap-2.5"
                    id="panel-btn-logo-brand"
                  >
                    <div className="w-8 h-8 rounded-lg bg-sky-500/20 text-sky-400 flex items-center justify-center shrink-0">
                      <Upload className="w-4 h-4" />
                    </div>
                    <div className="overflow-hidden">
                      <span className="text-xs font-bold text-sky-300 block truncate">Personalizar Marca 🎨</span>
                      <span className="text-[10px] text-slate-400 block leading-tight mt-0.5">
                        Logotipo, Favicon e Identidade
                      </span>
                    </div>
                  </button>
                )}
              </div>
            </div>

            {/* 5. 📝 CONTEÚDO, BLOG & ATENDIMENTO */}
            <div className="p-3 bg-slate-800/60 border border-slate-700/70 rounded-2xl space-y-2">
              <div className="flex items-center gap-2 pb-1.5 border-b border-slate-700/60">
                <BookOpen className="w-4 h-4 text-emerald-400" />
                <span className="text-xs font-black text-emerald-300 uppercase tracking-wider">Conteúdo & Atendimento</span>
              </div>

              <div className="space-y-1.5">
                {setActiveTab && (
                  <button
                    onClick={() => {
                      setActiveTab('videos');
                      setIsExpanded(false);
                    }}
                    className="w-full p-2 bg-slate-900/80 hover:bg-slate-900 border border-amber-500/40 hover:border-amber-400 rounded-xl text-left transition-all cursor-pointer flex items-center gap-2.5"
                    id="panel-btn-videos-admin"
                  >
                    <div className="w-7 h-7 rounded-lg bg-amber-500/20 text-amber-300 flex items-center justify-center shrink-0">
                      <Film className="w-4 h-4" />
                    </div>
                    <div className="overflow-hidden">
                      <span className="text-xs font-bold text-amber-200 block truncate">Vídeos Explicativos 🎬</span>
                      <span className="text-[10px] text-slate-400 block truncate">Inserir e editar vídeos tutoriais</span>
                    </div>
                  </button>
                )}

                {setActiveTab && (
                  <button
                    onClick={() => {
                      setActiveTab('blog');
                      setIsExpanded(false);
                    }}
                    className="w-full p-2 bg-slate-900/80 hover:bg-slate-900 border border-red-500/30 hover:border-red-400 rounded-xl text-left transition-all cursor-pointer flex items-center gap-2.5"
                    id="panel-btn-blog-admin"
                  >
                    <div className="w-7 h-7 rounded-lg bg-red-500/20 text-red-400 flex items-center justify-center shrink-0">
                      <BookOpen className="w-4 h-4" />
                    </div>
                    <div className="overflow-hidden">
                      <span className="text-xs font-bold text-red-200 block truncate">Blog & Guias Admin 📝</span>
                      <span className="text-[10px] text-slate-400 block truncate">Publicar artigos e tutoriais</span>
                    </div>
                  </button>
                )}

                {setActiveTab && (
                  <button
                    onClick={() => {
                      setActiveTab('support');
                      setIsExpanded(false);
                    }}
                    className="w-full p-2 bg-slate-900/80 hover:bg-slate-900 border border-emerald-500/30 hover:border-emerald-400 rounded-xl text-left transition-all cursor-pointer flex items-center gap-2.5"
                    id="panel-btn-support-admin"
                  >
                    <div className="w-7 h-7 rounded-lg bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0">
                      <Headphones className="w-4 h-4" />
                    </div>
                    <div className="overflow-hidden">
                      <span className="text-xs font-bold text-emerald-300 block truncate">Central de Suporte 🎧</span>
                      <span className="text-[10px] text-slate-400 block truncate">Tickets e atendimento ao cliente</span>
                    </div>
                  </button>
                )}
              </div>
            </div>

          </div>

        </div>
      ) : (
        /* ----------------- COLLAPSED FLOATING DOCK STATE (APENAS ACESSO RÁPIDO SOLICITADO) ----------------- */
        <div className="bg-slate-900/95 hover:bg-slate-900 backdrop-blur-md text-white border border-slate-700/80 shadow-2xl rounded-full p-1.5 sm:p-2 flex items-center gap-1.5 sm:gap-2 animate-fade-in border-t-2 border-t-amber-500 overflow-x-auto max-w-[95vw] whitespace-nowrap">
          
          {/* Main Toggle Button to Open the Full Suspended Admin Panel */}
          <button
            onClick={() => setIsExpanded(true)}
            className="px-3.5 py-2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-950 font-black text-xs rounded-full flex items-center gap-1.5 shadow-md transition-all cursor-pointer group shrink-0"
            title="Abrir Painel de Gestão Administrador Suspenso com todas as ferramentas"
            id="admin-dock-expand-btn"
          >
            <Shield className="w-4 h-4 text-slate-950 shrink-0" />
            <span className="inline">Painel Admin</span>
            <ChevronUp className="w-4 h-4 text-slate-950 group-hover:-translate-y-0.5 transition-transform" />
          </button>

          <div className="h-5 w-px bg-slate-700 my-auto shrink-0" />

          {/* 1. PEDIDOS DE ENVIOS */}
          {setActiveTab && (
            <button
              onClick={() => setActiveTab('admin-shipments')}
              className={`relative px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer shrink-0 ${
                activeTab === 'admin-shipments'
                  ? 'bg-sky-500 text-slate-950 font-black shadow-md'
                  : 'bg-slate-800 hover:bg-slate-750 text-sky-300 border border-sky-500/30'
              }`}
              title={`Pedidos de Envios Consolidados (${pendingShipmentsCount} em espera)`}
              id="dock-btn-shipments"
            >
              <Truck className="w-4 h-4 text-sky-400" />
              <span className="hidden sm:inline">Pedidos de Envios</span>
              {pendingShipmentsCount > 0 ? (
                <span className="px-1.5 py-0.5 text-[9px] font-black bg-rose-500 text-white rounded-full animate-pulse border border-slate-900 shadow-xs">
                  {pendingShipmentsCount}
                </span>
              ) : (
                <span className="text-[10px] text-slate-400 font-normal sm:hidden">Envios</span>
              )}
            </button>
          )}

          {/* 2. LISTA DE CLIENTES */}
          {setActiveTab && (
            <button
              onClick={() => setActiveTab('admin-clients')}
              className={`px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer shrink-0 ${
                activeTab === 'admin-clients'
                  ? 'bg-amber-500 text-slate-950 font-black shadow-md'
                  : 'bg-slate-800 hover:bg-slate-750 text-amber-300 border border-amber-500/30'
              }`}
              title="Lista de Clientes e Gestão de Suítes"
              id="dock-btn-all-clients"
            >
              <Users className="w-4 h-4 text-amber-400" />
              <span className="hidden sm:inline">Lista de Clientes</span>
              <span className="sm:hidden">Clientes</span>
            </button>
          )}

          {/* 3. PERSONAL SHOPPER (Piscando ou com alerta quando há novos pedidos) */}
          {setActiveTab && (
            <button
              onClick={() => setActiveTab('personal-shopper')}
              className={`relative px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer shrink-0 ${
                activeTab === 'personal-shopper'
                  ? 'bg-indigo-600 text-white font-black shadow-md'
                  : pendingPsOrdersCount > 0
                    ? 'bg-gradient-to-r from-amber-500/30 via-rose-500/30 to-indigo-500/30 text-white border-2 border-amber-400 animate-pulse font-black shadow-lg shadow-amber-500/20'
                    : 'bg-slate-800 hover:bg-slate-750 text-indigo-300 border border-indigo-500/30'
              }`}
              title={`Personal Shopper (${pendingPsOrdersCount} pedidos pendentes)`}
              id="dock-btn-personal-shopper"
            >
              <ShoppingBag className={`w-4 h-4 ${pendingPsOrdersCount > 0 ? 'text-amber-300 animate-bounce' : 'text-indigo-400'}`} />
              <span className="hidden sm:inline">Personal Shopper</span>
              <span className="sm:hidden">Shopper</span>
              {pendingPsOrdersCount > 0 && (
                <span className="flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-black bg-rose-600 text-white rounded-full animate-pulse shadow-xs">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-300 animate-ping" />
                  {pendingPsOrdersCount}
                </span>
              )}
            </button>
          )}

          {/* 4. ESTOQUE DE PACOTES */}
          {setActiveTab && (
            <button
              onClick={() => setActiveTab('admin-packages')}
              className={`px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer shrink-0 ${
                activeTab === 'admin-packages'
                  ? 'bg-emerald-500 text-slate-950 font-black shadow-md'
                  : 'bg-slate-800 hover:bg-slate-750 text-emerald-300 border border-emerald-500/30'
              }`}
              title="Estoque Geral de Pacotes no Depósito"
              id="dock-btn-packages"
            >
              <Box className="w-4 h-4 text-emerald-400" />
              <span className="hidden sm:inline">Estoque de Pacotes</span>
              <span className="sm:hidden">Estoque</span>
            </button>
          )}

          {/* 5. FINANCEIRO E BALANÇO */}
          {setActiveTab && (
            <button
              onClick={() => setActiveTab('admin-finances')}
              className={`px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer shrink-0 ${
                activeTab === 'admin-finances'
                  ? 'bg-emerald-500 text-slate-950 font-black shadow-md'
                  : 'bg-slate-800 hover:bg-slate-750 text-emerald-300 border border-emerald-500/30'
              }`}
              title="Financeiro e Balanço Geral"
              id="dock-btn-finances"
            >
              <DollarSign className="w-4 h-4 text-emerald-400" />
              <span className="hidden sm:inline">Financeiro & Balanço</span>
              <span className="sm:hidden">Financeiro</span>
            </button>
          )}

          {/* 6. DAR ENTRADA EM PACOTE */}
          {onOpenAdminEntry && (
            <button
              onClick={onOpenAdminEntry}
              className="px-3.5 py-1.5 bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-slate-950 font-black text-xs rounded-full flex items-center gap-1.5 transition-all hover:scale-105 cursor-pointer shadow-md shrink-0"
              title="Dar Entrada em Pacote no Depósito"
              id="dock-btn-entry"
            >
              <PackagePlus className="w-4 h-4 text-slate-950 shrink-0" />
              <span>Dar Entrada</span>
            </button>
          )}

        </div>
      )}

    </div>
  );
};

export default AdminSidebar;
